import { useRef, useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, ChevronUp, X, Check } from 'lucide-react';

export type Option = {
  value: string;
  label: string;
};

type Props = {
  options: Array<Option> | undefined;
  value: Array<Option>;
  title: string;
  isLoading: boolean;
  isMulti?: boolean;
  showSelections?: boolean;
  onChange?: (values: Array<Option>) => void;
};

const CheckboxSelect = ({ value, options, title, onChange, showSelections = false }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focused]);

  const processPastedText = (pastedText: string) => {
    if (!pastedText || !options || !onChange) return;

    const normalize = (s: string) => s.trim().toLowerCase();

    const items = pastedText
      .split(/[\n\r\t]+/)
      .map(item => item.trim())
      .filter(Boolean);

    if (!items.length) return;

    const normalizedItems = items.map(normalize);

    const matchedOptions = options.filter(option => {
      const v = normalize(option.value);
      const l = normalize(option.label);
      return normalizedItems.includes(v) || normalizedItems.includes(l);
    });

    if (matchedOptions.length > 0) {
      const newValues = [
        ...value,
        ...matchedOptions.filter(
          option => !value.some(v => v.value === option.value)
        )
      ];

      onChange(newValues);
      setInputValue('');
      setFocused(false);
      // keep dropdown open, but scroll to top so selected items (sorted first) are visible
      requestAnimationFrame(() => {
        contentRef.current?.scrollTo({ top: 0 });
      });
    }
  };

  const handlePasteFromClipboard = async () => {
    const pastedText = await navigator.clipboard.readText();
    processPastedText(pastedText);
  };

  useEffect(() => {
    const handleGlobalPaste = (event: KeyboardEvent) => {
      if (!open) return;

      if ((event.ctrlKey || event.metaKey) && event.key === 'v') {
        event.preventDefault();
        event.stopPropagation();

        if (!focused) setFocused(true);
        handlePasteFromClipboard();
      }
    };

    if (open) {
      window.addEventListener('keydown', handleGlobalPaste);
      return () => {
        window.removeEventListener('keydown', handleGlobalPaste);
      };
    }
  }, [open, focused]);

  const handleToggleOpen = () => {
    const newOpen = !open;
    setOpen(newOpen);
    setFocused(newOpen);
    if (!newOpen) setInputValue('');
  };

  const handleDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!focused) {
      setFocused(true);
      if (!open) {
        setOpen(true);
      }
    }
  };

  const handleSelect = (optionValue: string) => {
    if (!onChange || !options) return;

    const isSelected = value.some(selectedOption => selectedOption.value === optionValue);

    onChange(
      isSelected
        ? value.filter(selectedOption => selectedOption.value !== optionValue)
        : [...value, options.find(option => option.value === optionValue)!]
    );
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange?.([]);
  };

  const handleInputChange = (input: string) => {
    setInputValue(input);
    if (!options || !onChange) return;

    // Optional: allow typing multiple exact values separated by space/comma to select them
    const items = input
      .split(/[,\s]+/)
      .map(item => item.trim())
      .filter(Boolean);

    if (items.length < 2) return;

    const normalize = (s: string) => s.trim().toLowerCase();
    const normalizedItems = items.map(normalize);
    const selectedOptions = options.filter(option => normalizedItems.includes(normalize(option.value)));

    if (selectedOptions.length) {
      const newValues = [
        ...value,
        ...selectedOptions.filter(option => !value.some(v => v.value === option.value)),
      ];
      onChange(newValues);
      setInputValue('');
    }
  };

  const handleInputBlur = () => {
    // Не закрываем input сразу, даем время для обработки вставки
    setTimeout(() => {
      setFocused(false);
    }, 200);
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const pastedText = event.clipboardData.getData('text');
    processPastedText(pastedText);
  };

  const getDisplayValue = () => {
    if (value.length === 0) return '';
    if (showSelections) return value.map(option => option.label).join(', ');
    if (value.length === 1) return value[0].label;

    return `${value.length} элемент(а)`;
  };

  const handleIconClick = (event: React.MouseEvent) => {
    if (value.length > 0) {
      event.stopPropagation();
      handleClear(event);
    }
  };

  const handleOptionClick = (event: React.MouseEvent, optionValue: string) => {
    event.stopPropagation();
    handleSelect(optionValue);
  };

  const selectedValues = value.map(value => value.value);
  const selectedSet = new Set(selectedValues);
  const optionsListRaw = options || [];
  const optionsList = optionsListRaw
    .map((opt, idx) => ({ opt, idx }))
    .sort((a, b) => {
      const aSelected = selectedSet.has(a.opt.value);
      const bSelected = selectedSet.has(b.opt.value);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      return a.idx - b.idx;
    })
    .map(x => x.opt);

  const normalizedQuery = inputValue.trim().toLowerCase();
  const filteredOptionsList = normalizedQuery
    ? optionsList.filter(option => {
        const v = option.value.toLowerCase();
        const l = option.label.toLowerCase();
        return v.includes(normalizedQuery) || l.includes(normalizedQuery);
      })
    : optionsList;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        className="rounded-lg p-[10px] bg-gray-800 cursor-pointer relative text-[12px] h-[34px] inline-flex items-center gap-[20px]"
        onClick={handleToggleOpen}
      >
        <Popover.Trigger asChild>
          <div className="flex items-center gap-[20px] min-w-0 w-full">
            {!value.length ? (
              <span
                className="text-white text-[12px] whitespace-nowrap"
                onDoubleClick={handleDoubleClick}
                title="Откройте список, затем ищите или вставляйте (Ctrl+V)"
              >
                {title}
              </span>
            ) : (
              <span
                className="text-white text-[12px] overflow-hidden text-ellipsis whitespace-nowrap"
                onDoubleClick={handleDoubleClick}
                title="Откройте список, затем ищите или вставляйте (Ctrl+V)"
              >
                {getDisplayValue()}
              </span>
            )}
            <div className="flex-shrink-0 cursor-pointer" onClick={handleIconClick}>
              {value.length > 0 ? (
                <X size={16} color="white" />
              ) : open ? (
                <ChevronUp size={16} color="white" />
              ) : (
                <ChevronDown size={16} color="white" />
              )}
            </div>
          </div>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            ref={contentRef}
            className="bg-gray-800 text-white rounded-md overflow-y-auto z-50 py-2 px-[10px] outline-none max-h-[420px]"
            align="start"
            sideOffset={14}
            alignOffset={-10}
            onOpenAutoFocus={(e) => {
              // Предотвращаем автофокус на popover, чтобы пользователь мог сразу выбрать из списка
              e.preventDefault();
            }}
          >
            <div className="sticky top-0 bg-gray-800 pt-1 pb-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={event => handleInputChange(event.target.value)}
                onPaste={handlePaste}
                onBlur={handleInputBlur}
                className="w-full bg-gray-900 text-white text-[12px] outline-none px-2 py-2 rounded-md"
                placeholder="Поиск / вставка (Ctrl+V)"
                onClick={e => e.stopPropagation()}
              />
            </div>
            {filteredOptionsList.map(option => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <div
                  key={option.value}
                  className="flex items-center gap-[10px] cursor-pointer px-3 py-2 rounded hover:bg-[#2a2d32] outline-none"
                  onClick={event => handleOptionClick(event, option.value)}
                >
                  <div
                    className={`w-[14px] h-[14px] rounded-sm border-2 border-solid border-gray-600 flex items-center justify-center ${isSelected ? 'bg-blue-500 border-blue-500' : ''}`}
                  >
                    {isSelected && <Check size={12} color="white" />}
                  </div>
                  <div>{option.label}</div>
                </div>
              );
            })}
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
};

export default CheckboxSelect;
